import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Input } from 'components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { 
  Users, Clock, UserCheck, UserPlus, Settings, Loader2, BarChart3,
  ChevronRight, MapPin, Search, Filter, TrendingUp, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

export default function AgencyDashboard() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/agency/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  const stats = dashboard?.stats || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="agency-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
              <Users className="w-6 h-6 text-violet-600" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']">Agency Portal</h1>
              <p className="text-xs sm:text-sm text-slate-500">{dashboard?.profile?.agency_name || user?.name}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/settings')}>
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Caregivers</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_caregivers || 0}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Available</p>
                  <p className="text-2xl font-bold text-green-600">{stats.available_caregivers || 0}</p>
                </div>
                <UserCheck className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Active Placements</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.active_placements || 0}</p>
                </div>
                <MapPin className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Hours This Month</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_hours_this_month || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-teal-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Compliance</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.compliance_rating || 100}%</p>
                </div>
                <TrendingUp className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="caregivers" className="space-y-4">
          <TabsList>
            <TabsTrigger value="caregivers">Caregivers</TabsTrigger>
            <TabsTrigger value="placements">Placements</TabsTrigger>
            <TabsTrigger value="hours">Hours Report</TabsTrigger>
          </TabsList>

          <TabsContent value="caregivers" className="space-y-4">
            {/* Search */}
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search caregivers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button variant="outline">
                <Filter className="w-4 h-4 mr-2" />
                Filter
              </Button>
              <Button>
                <UserPlus className="w-4 h-4 mr-2" />
                Add Caregiver
              </Button>
            </div>

            {/* Caregiver Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(dashboard?.caregivers || []).map((cg) => (
                <Card key={cg.user_id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {cg.picture ? (
                          <img src={cg.picture} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-lg font-bold text-teal-700">{cg.name?.charAt(0) || '?'}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-slate-900 truncate">{cg.name || 'Unknown'}</h4>
                        <p className="text-sm text-slate-500 truncate">{cg.email}</p>
                        <div className="flex items-center gap-2 mt-2">
                          {cg.verified && <Badge className="bg-green-100 text-green-700 text-xs">Verified</Badge>}
                          {cg.current_placement ? (
                            <Badge variant="secondary" className="text-xs">Placed</Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-700 text-xs">Available</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 pt-3 border-t flex justify-between text-sm">
                      <span className="text-slate-500">{cg.total_hours || 0} hrs total</span>
                      <span className="text-slate-500">${cg.hourly_rate || '—'}/hr</span>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {(dashboard?.caregivers?.length || 0) === 0 && (
                <Card className="col-span-full">
                  <CardContent className="py-12 text-center">
                    <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-500">No caregivers in your agency yet</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="placements">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Active Placements</CardTitle>
                <Button size="sm">New Placement</Button>
              </CardHeader>
              <CardContent>
                {(dashboard?.placements?.length || 0) === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <MapPin className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No active placements</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {dashboard.placements.map((placement) => (
                      <div key={placement.placement_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">{placement.caregiver_name || placement.caregiver_id}</p>
                          <p className="text-sm text-slate-500">→ {placement.family_name || placement.family_id}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">{placement.hours_per_week} hrs/week</p>
                          <Badge>{placement.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="hours">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-teal-500" />
                  Hours Report
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {(dashboard?.recent_shifts || []).slice(0, 10).map((shift) => (
                    <div key={shift.shift_id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">{shift.caregiver_id}</p>
                        <p className="text-sm text-slate-500">{new Date(shift.clock_in).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{shift.total_hours?.toFixed(1) || '—'} hrs</p>
                        <Badge variant={shift.status === 'approved' ? 'default' : 'secondary'}>{shift.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
